import {
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useGetPurchases } from "@entities/accounting/lib/hooks/useGetPurchases";
import { useRaiseCreditNoteOnInvoice } from "@entities/accounting/lib/hooks/useRaiseCreditNoteOnInvoice";
import {
  ICreditNoteOnInvoice,
  IGetPurchase,
} from "@entities/accounting/lib/types";
import { FirebaseService, ToastService } from "@shared/lib/services";
import { Money } from "@shared/lib/services/Money.service";
import { IBrandAccounting } from "@shared/lib/types/accounting";
import { LEDGER_TYPE } from "@shared/lib/types/briefLedger";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Steps,
  Table,
  Typography,
  Upload,
} from "antd";
import { ColumnsType } from "antd/es/table";
import { CircleAlert } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";

const STORAGE_PATH = "brand-credit-notes";
const ACCEPT_TYPES = ".pdf,.jpg,.jpeg,.png";
const MAX_SIZE_MB = 5;

interface AddCreditNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: IBrandAccounting | null;
}

export const AddCreditNoteModal: React.FC<AddCreditNoteModalProps> = ({
  isOpen,
  onClose,
  invoice,
}) => {
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [selectedPurchases, setSelectedPurchases] = useState<React.Key[]>([]);
  const [purchaseSearch, setPurchaseSearch] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const { raiseCreditNote, isRaising, isRaised, raiseCreditNoteError } =
    useRaiseCreditNoteOnInvoice();

  const { purchases, isFetching: arePurchasesLoading } = useGetPurchases({
    accountingIds: invoice ? [invoice._id] : [],
  });

  useEffect(() => {
    if (isRaised) {
      ToastService.success("Credit note added successfully");
      onClose();
    } else if (raiseCreditNoteError) {
      ToastService.error(raiseCreditNoteError.message);
    }
  }, [isRaised]);

  const filteredPurchases = useMemo(
    () =>
      purchases?.filter(
        (p) =>
          p.remarks?.toLowerCase().includes(purchaseSearch.toLowerCase()) ||
          p._id?.toLowerCase().includes(purchaseSearch.toLowerCase())
      ),
    [purchases, purchaseSearch]
  );

  const selectedPurchaseData =
    purchases?.filter((p) => selectedPurchases.includes(p._id)) ?? [];

  const totalSelectedTotalAmount = selectedPurchaseData
    .reduce(
      (acc, curr) =>
        curr.type === LEDGER_TYPE.Credit
          ? acc.subtract(new Money(curr.totalAmount))
          : acc.add(new Money(curr.totalAmount)),
      new Money(0)
    )
    .toNumber();

  const totalSelectedBaseCost = selectedPurchaseData
    .reduce(
      (acc, curr) =>
        curr.type === LEDGER_TYPE.Credit
          ? acc.subtract(new Money(curr.baseCost))
          : acc.add(new Money(curr.baseCost)),
      new Money(0)
    )
    .toNumber();
  const totalSelectedGstAmount = selectedPurchaseData
    .reduce(
      (acc, curr) =>
        curr.type === LEDGER_TYPE.Credit
          ? acc.subtract(new Money(curr.gstAmount))
          : acc.add(new Money(curr.gstAmount)),
      new Money(0)
    )
    .toNumber();

  const handleNext = () => {
    // if (step === 0 && selectedPurchases.length === 0) {
    //   return;
    // }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const resetState = () => {
    setStep(0);
    setSelectedPurchases([]);
    setPurchaseSearch("");
    setUploadedFile(null);
    setUploadedFileUrl(null);
    form.resetFields();
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  const handleBeforeUpload = (file: File): boolean => {
    const isLt5M = file.size / 1024 / 1024 < MAX_SIZE_MB;
    if (!isLt5M) {
      ToastService.error(`File must be smaller than ${MAX_SIZE_MB}MB!`);
      return false;
    }
    setIsUploading(true);
    FirebaseService.uploadFileToFirebase(
      `${STORAGE_PATH}/${invoice?._id}/${file.name}${Date.now()}`,
      file
    )
      .then((url) => {
        setUploadedFile(file);
        setUploadedFileUrl(url);
        ToastService.success("File uploaded successfully");
      })
      .catch(() => {
        ToastService.error("File upload failed. Please try again.");
      })
      .finally(() => {
        setIsUploading(false);
      });
    return false; // Prevent default upload, we handle via Firebase
  };

  const handleSubmit = async () => {
    if (!invoice) return;

    try {
      const values = await form.validateFields();
      if (!uploadedFileUrl) {
        ToastService.error("Please upload the credit note document.");
        return;
      }
      const payload: ICreditNoteOnInvoice = {
        creditedToAccountingId: invoice._id,
        baseAmount: new Money(totalSelectedBaseCost).toNumber(),
        gstAmount: new Money(totalSelectedGstAmount).toNumber(),
        totalAmount: new Money(totalSelectedTotalAmount).toNumber(),
        brandLedgerIds: selectedPurchases as string[],
        date: values.cnDate.toISOString(),
        url: uploadedFileUrl,
        accountingNumber: values.cnNumber,
        remarks: values.cnReason,
      };
      raiseCreditNote(payload);
    } catch (error) {
      console.error("Validation Failed:", error);
    }
  };

  const purchaseColumns: ColumnsType<IGetPurchase> = [
    {
      title: "Purchase Details",
      dataIndex: "remarks",
      render: (text: string, record: IGetPurchase) => (
        <div>
          <div>{text}</div>
          <div style={{ fontSize: 11, color: "#888" }}>
            {new Date(record.createdAt).toLocaleDateString("en-GB")} •{" "}
            {record._id}
          </div>
        </div>
      ),
    },
    {
      title: "Purchase",
      dataIndex: "sellingCost",
      align: "right",
      render: (val: number, record) => (
        <span
          className={`${record.type === "Credit" ? styles.amountNegative : ""}`}
        >
          {record.type === "Credit" ? "-" : ""}₹{val.toLocaleString()}
        </span>
      ),
    },
    {
      title: "Service",
      dataIndex: "serviceCost",
      align: "right",
      render: (val: number, record) => (
        <span
          className={`${record.type === "Credit" ? styles.amountNegative : ""}`}
        >
          {record.type === "Credit" ? "-" : ""}₹{val.toLocaleString()}
        </span>
      ),
    },
    {
      title: "GST",
      dataIndex: "gstAmount",
      align: "right",
      render: (val: number, record) => (
        <span
          className={`${record.type === "Credit" ? styles.amountNegative : ""}`}
        >
          {record.type === "Credit" ? "-" : ""}₹{val.toLocaleString()}
        </span>
      ),
    },
    {
      title: "Total",
      dataIndex: "totalAmount",
      align: "right",
      render: (val: number, record) => (
        <span
          className={`${record.type === "Credit" ? styles.amountNegative : ""}`}
        >
          {record.type === "Credit" ? "-" : ""}₹{val.toLocaleString()}
        </span>
      ),
    },
  ];

  const summaryColumns: ColumnsType<IGetPurchase> = [
    {
      title: "Purchase Ref",
      dataIndex: "remarks",
      render: (text: string, record: IGetPurchase) => (
        <div>
          <div>{text}</div>
          <div style={{ fontSize: 11, color: "#888" }}>
            {new Date(record.createdAt).toLocaleDateString("en-GB")} •{" "}
            {record._id}
          </div>
        </div>
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      align: "right",
      render: (val: number, record) =>
        `${record.type === "Credit" ? "-" : ""}₹${val.toLocaleString()}`,
    },
  ];

  if (!invoice) return null;

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={720}
      closeIcon={false}
      destroyOnClose
      centered
      styles={{
        body: { padding: 0 },
        content: { padding: 0, borderRadius: 20 },
      }}
    >
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div>
            <h2 className={styles.title}>Add Credit Note</h2>
            <p className={styles.subtitle}>
              For Invoice #{invoice.accountingNumber}
            </p>
          </div>
          <div className={styles.stepDivider} />
          <div className={styles.stepIndicatorWrap}>
            <Steps
              current={step}
              size="small"
              items={[
                { title: "Select Purchases" },
                { title: "Add Details & Summary" },
              ]}
            />
          </div>
        </div>
        <Button
          type="text"
          onClick={handleCancel}
          className={styles.closeBtn}
          aria-label="Close"
        >
          <CloseOutlined />
        </Button>
      </div>

      <div className={styles.body}>
        {step === 0 && (
          <div className={styles.stepContent}>
            <div className={styles.stepHeader}>
              <label className={styles.stepLabel}>Select Purchases</label>
              <span className={styles.count}>
                {selectedPurchases.length} selected
              </span>
            </div>
            <Alert
              message="All selected purchases will be marked as unbilled upon credit note creation."
              type="warning"
              showIcon
            />
            <Input
              placeholder="Search purchases..."
              prefix={<SearchOutlined className={styles.searchIcon} />}
              value={purchaseSearch}
              onChange={(e) => setPurchaseSearch(e.target.value)}
              className={styles.searchInput}
              allowClear
            />
            <div className={styles.creditNoteTable}>
              <Table
                rowKey="_id"
                columns={purchaseColumns}
                dataSource={filteredPurchases}
                loading={arePurchasesLoading}
                rowSelection={{
                  selectedRowKeys: selectedPurchases,
                  onChange: setSelectedPurchases,
                }}
                pagination={false}
                size="small"
                scroll={{ y: 240 }}
              />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className={styles.stepContent}>
            <div className={styles.summarySection}>
              <div className={styles.summaryHeader}>
                <h4 className={styles.summaryTitle}>Credit Note Summary</h4>
                <span className={styles.summaryMeta}>
                  {selectedPurchases.length} purchase(s)
                </span>
              </div>
              <div className={styles.creditNoteTable}>
                <Table
                  size="small"
                  className={styles.summaryTable}
                  rowKey="_id"
                  columns={summaryColumns}
                  dataSource={selectedPurchaseData}
                  pagination={false}
                  summary={() => (
                    <Table.Summary.Row className={styles.summaryFooter}>
                      <Table.Summary.Cell index={0}>
                        Total Selected Purchases
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={1}
                        align="right"
                        className={styles.summaryTotal}
                      >
                        ₹{totalSelectedTotalAmount.toLocaleString()}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </div>
            </div>

            <Typography.Text type="danger">
              <CircleAlert size={12} style={{ marginRight: 5 }} />
              Total credit amount must be equal to ₹
              {totalSelectedTotalAmount.toLocaleString()}
            </Typography.Text>

            <Form
              form={form}
              layout="vertical"
              className={styles.form}
              requiredMark={false}
            >
              <Form.Item label="Upload Credit Note" required>
                <Upload.Dragger
                  name="file"
                  multiple={false}
                  accept={ACCEPT_TYPES}
                  showUploadList={false}
                  beforeUpload={handleBeforeUpload}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <p>Uploading...</p>
                  ) : uploadedFile ? (
                    <p>
                      {uploadedFile.name} (
                      {(uploadedFile.size / 1024).toFixed(1)} KB)
                    </p>
                  ) : (
                    <>
                      <p className="ant-upload-drag-icon">
                        <UploadOutlined />
                      </p>
                      <p className="ant-upload-text">
                        Click or drag file to this area to upload
                      </p>
                      <p className="ant-upload-hint">
                        PDF, JPG or PNG (Max 5MB)
                      </p>
                    </>
                  )}
                </Upload.Dragger>
              </Form.Item>

              <Row gutter={10}>
                <Col span={12}>
                  <Form.Item
                    label="Credit Note Number"
                    name="cnNumber"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="CN-2024-001" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Credit Note Date"
                    name="cnDate"
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Reason for Credit Note"
                name="cnReason"
                rules={[{ required: true }]}
              >
                <Input.TextArea placeholder="Enter reason..." rows={3} />
              </Form.Item>

              <Form.Item
                label="Total Amount (Incl. Tax)"
                name="cnAmount"
                rules={[
                  { required: true, message: "Please input the amount!" },
                  {
                    validator: (_, value) =>
                      !new Money(value).equals(
                        new Money(totalSelectedTotalAmount)
                      )
                        ? Promise.reject(
                            new Error(
                              `Amount must be ${totalSelectedTotalAmount.toLocaleString()}`
                            )
                          )
                        : Promise.resolve(),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  prefix="₹"
                  placeholder="0.00"
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value!.replace(/₹\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Form>
          </div>
        )}
      </div>

      <div
        className={styles.footer}
        style={{ justifyContent: step < 1 ? "flex-end" : "space-between" }}
      >
        {step > 0 && (
          <Button
            icon={<LeftOutlined />}
            onClick={handleBack}
            disabled={step === 0 || isRaising}
            className={styles.backBtn}
          >
            Back
          </Button>
        )}
        {step < 1 ? (
          <Button
            type="primary"
            icon={<RightOutlined />}
            iconPosition="end"
            onClick={handleNext}
            disabled={selectedPurchases.length === 0}
            className={styles.nextBtn}
          >
            Next
          </Button>
        ) : (
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={isRaising}
            disabled={!uploadedFileUrl || isUploading}
            className={styles.submitBtn}
          >
            {isRaising ? "Submitting..." : "Add Credit Note"}
          </Button>
        )}
      </div>
    </Modal>
  );
};
